import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import useSign from '@/pages/SignLayout.context';
import useApi from '@/hooks/useApi';
import Button from '@/components/Button';
import FormError from '@/components/FormError';
import FormPassword from '@/components/FormPassword';
import LoginGuardShell from './LoginGuardShell';
import styles from './Login.module.scss';

type LoginInputs = {
  password: string;
};

const LoginPassword = () => {
  const { t } = useTranslation(['sign', 'common']);
  const { signerId, workflowId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const { setLoginSuccessful } = useSign();
  const [verifyAccess] = useApi('sign.verify');

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginInputs>();

  const doLogin: SubmitHandler<LoginInputs> = async (inputs) => {
    setLoading(true);
    const res = await verifyAccess({
      password: inputs.password,
      workflowId: workflowId ?? '',
    });
    const token = res?.response?.data.verificationToken;
    if (token) {
      sessionStorage.setItem('sudoToken', token);
      setLoginSuccessful();
    } else {
      setError('root', { message: t('sign:fields.password.invalid') });
    }
    setLoading(false);
  };

  const handleCancel = () => {
    void navigate(`/sign/${workflowId}/${signerId}/cancel`, { replace: true });
  };

  return (
    <LoginGuardShell introKey="sign:login.guard.passwordIntro">
      <form onSubmit={handleSubmit(doLogin)} className={styles.form}>
        <FormPassword
          autoComplete="current-password"
          className={styles.passwordWrapper}
          errorMessage={errors.password?.message}
          id="password"
          label={t('sign:fields.password')}
          placeholder={t('sign:fields.passwordPlaceholder')}
          {...register('password', { required: true })}
        />
        {errors.root?.message && (
          <FormError isRoot message={errors.root.message} />
        )}
        <div className={styles.actions}>
          <Button
            variant="alert-muted"
            size="slim"
            onClick={handleCancel}
            disabled={loading}
            className={styles.actionButton}
          >
            {t('common:button.cancel')}
          </Button>
          <Button
            submit
            variant="primary"
            size="slim"
            loading={loading}
            className={styles.actionButton}
          >
            {t('sign:login.submit')}
          </Button>
        </div>
      </form>
    </LoginGuardShell>
  );
};

export default LoginPassword;
